import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  ChevronDown,
  TrendingUp,
  Shield,
  Users,
  Award,
  Bitcoin,
  BarChart3
} from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTradingDropdown, setShowTradingDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { 
      name: 'Trading',
      href: '#',
      dropdown: [
        { name: 'Stock Trading', href: '/stock-trading', icon: <TrendingUp className="w-4 h-4" /> },
        { name: 'Crypto Trading', href: '/crypto-trading', icon: <Bitcoin className="w-4 h-4" /> },
        { name: 'All Markets', href: '/', icon: <BarChart3 className="w-4 h-4" /> }
      ]
    },
    { name: 'Services', href: '/services' },
    { name: 'Copy Trading', href: '/copy-trading' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo">
          <div className="header-logo-icon">
            <TrendingUp className="header-logo-svg" />
          </div>
          <span className="header-logo-text">ChartLine</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav">
          {navigation.map((item) => (
            <div key={item.name} className="header-nav-item">
              {item.dropdown ? (
                <div 
                  className="header-dropdown-wrapper"
                  onMouseEnter={() => setShowTradingDropdown(true)}
                  onMouseLeave={() => setShowTradingDropdown(false)}
                >
                  <button className="header-nav-link header-dropdown-trigger">
                    {item.name}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </button>
                  
                  <AnimatePresence>
                    {showTradingDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="header-dropdown"
                      >
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="header-dropdown-item"
                            onClick={() => setShowTradingDropdown(false)}
                          >
                            {dropdownItem.icon}
                            <span>{dropdownItem.name}</span>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`header-nav-link ${isActive(item.href) ? 'active' : ''}`}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="header-cta">
          <a 
            href="https://dashboard.chartlinebrokerage.com"
            target="_blank"
            rel="noopener noreferrer"
            className="header-cta-secondary"
          >
            Sign In
          </a>
          <a 
            href="https://dashboard.chartlinebrokerage.com"
            target="_blank"
            rel="noopener noreferrer"
            className="header-cta-primary"
          >
            Get Started
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="header-mobile-button"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <X className="header-mobile-icon" />
          ) : (
            <Menu className="header-mobile-icon" />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="header-mobile-menu"
          >
            <nav className="header-mobile-nav">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.dropdown ? (
                    <>
                      <div className="header-mobile-dropdown-title">{item.name}</div>
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="header-mobile-dropdown-item"
                          onClick={() => setIsOpen(false)}
                        >
                          {dropdownItem.icon}
                          <span>{dropdownItem.name}</span>
                        </Link>
                      ))}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`header-mobile-link ${isActive(item.href) ? 'active' : ''}`}
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="header-mobile-cta">
                <a 
                  href="https://dashboard.chartlinebrokerage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header-mobile-cta-secondary"
                  onClick={() => setIsOpen(false)}
                >
                  Sign In
                </a>
                <a 
                  href="https://dashboard.chartlinebrokerage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header-mobile-cta-primary"
                  onClick={() => setIsOpen(false)}
                >
                  Get Started
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header; 